import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Building2,
  Check,
  Copy,
  KeyRound,
  Plus,
  Search,
  Send,
  ShieldCheck,
  Sparkles,
  UserPlus,
  Users,
} from 'lucide-react';
import { SUBSCRIPTION_PLANS } from '@blogforge/shared';
import type {
  AdminCreateUserResult,
  AdminSendMetrics,
  AdminSendMetricsOverview,
  AdminUserSummary,
  AdminWorkspaceSummary,
  EmailSendStatus,
  SubscriptionPlanId,
} from '@blogforge/shared';
import { api, ApiError } from '../hooks/api.js';

// ─────────────────────────────────────────────────────────────────
// Platform admin (/admin)
//
// The operator surface for private beta. There is no public signup form, so
// this is the only way an account comes into existence: an operator creates
// the account and its workspace, then hands over the generated password.
// The created user is always the **owner** of that workspace — which is what
// puts them through the onboarding wizard on their first sign-in.
//
// Three tabs: Accounts (who can sign in), Workspaces (who is on what plan) and
// Sends (how delivery is going across the deployment). All are read from the
// admin API, which re-checks `users.is_platform_admin` on every request.
//
// The Sends tab reports counts and a timeline and deliberately shows no
// recipient data — see apps/server/src/admin/service.ts for why.
// ─────────────────────────────────────────────────────────────────

type Tab = 'users' | 'workspaces' | 'sends';

const PAGE_SIZE = 20;

export function AdminPage() {
  const [tab, setTab] = useState<Tab>('users');

  const overview = useQuery({
    queryKey: ['admin-overview'],
    queryFn: () => api.admin.overview(),
  });

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto w-full max-w-[1080px] px-6 py-8">
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="eyebrow">
              <ShieldCheck size={12} /> System administration
            </p>
            <h1 className="display mt-3 text-[2rem]">This instance</h1>
            <p className="text-ink-soft text-body-sm mt-2 max-w-[560px]">
              Accounts and workspaces on this deployment. BlogForger has no public signup — every
              account here was created by an administrator.
            </p>
          </div>
        </header>

        {/* ── Counters ──────────────────────────────────────────── */}
        {overview.data && (
          <div className="mt-8 grid gap-3 sm:grid-cols-3">
            <Counter
              label="Accounts"
              value={overview.data.users.total}
              hint={`${overview.data.users.neverSignedIn} never signed in`}
            />
            <Counter
              label="Workspaces"
              value={overview.data.workspaces.total}
              hint={`${overview.data.workspaces.awaitingSetup} awaiting setup`}
            />
            <Counter
              label="On a paid plan"
              value={overview.data.workspaces.onPaidPlan}
              hint={`${overview.data.users.platformAdmins} platform admin${overview.data.users.platformAdmins === 1 ? '' : 's'}`}
            />
          </div>
        )}

        {/* ── Tabs ──────────────────────────────────────────────── */}
        <div className="border-line mt-8 flex items-center gap-1 border-b">
          {[
            { id: 'users' as const, label: 'Accounts', icon: Users },
            { id: 'workspaces' as const, label: 'Workspaces', icon: Building2 },
            { id: 'sends' as const, label: 'Sends', icon: Send },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              className={`-mb-px flex items-center gap-2 border-b-2 px-4 py-2.5 text-[13px] font-semibold transition-colors ${
                tab === id
                  ? 'border-accent text-accent'
                  : 'text-muted hover:text-ink border-transparent'
              }`}
            >
              <Icon size={14} />
              {label}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {tab === 'users' && <UsersTab />}
          {tab === 'workspaces' && <WorkspacesTab />}
          {tab === 'sends' && <SendsTab />}
        </div>
      </div>
    </div>
  );
}

function Counter({ label, value, hint }: { label: string; value: number; hint: string }) {
  return (
    <div className="glass-card p-4">
      <p className="text-muted font-mono text-[10.5px] tracking-[0.04em] uppercase">{label}</p>
      <p className="mt-1 text-[26px] leading-none font-bold">{value}</p>
      <p className="text-ink-soft mt-2 text-[11.5px]">{hint}</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Accounts
// ─────────────────────────────────────────────────────────────────

function UsersTab() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [created, setCreated] = useState<AdminCreateUserResult | null>(null);

  const query = useQuery({
    queryKey: ['admin-users', { page, search }],
    queryFn: () => api.admin.users({ page, limit: PAGE_SIZE, ...(search ? { search } : {}) }),
  });

  const users = query.data?.users ?? [];

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative min-w-[220px] flex-1 sm:max-w-[320px]">
          <Search size={14} className="text-muted absolute top-1/2 left-3 -translate-y-1/2" />
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search name or email"
            aria-label="Search accounts"
            className="field-input pl-9"
          />
        </div>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="btn-primary px-4 py-2 text-[13px]"
        >
          <UserPlus size={14} />
          Create account
        </button>
      </div>

      {query.isLoading && <p className="text-muted mt-6 text-[13px]">Loading accounts…</p>}
      {query.isError && (
        <p className="text-danger mt-6 text-[13px]">
          Could not load accounts. {query.error instanceof ApiError ? query.error.message : ''}
        </p>
      )}

      {!query.isLoading && users.length === 0 && (
        <div className="glass-card mt-6 p-8 text-center">
          <p className="text-[14px] font-semibold">
            {search ? 'No accounts match that search.' : 'No accounts yet.'}
          </p>
          {!search && (
            <p className="text-ink-soft text-body-sm mt-1">
              Create the first one — it becomes the owner of its own workspace.
            </p>
          )}
        </div>
      )}

      {users.length > 0 && (
        <div className="mt-6 space-y-2">
          {users.map((user) => (
            <UserRow key={user.id} user={user} />
          ))}
        </div>
      )}

      {query.data && query.data.totalPages > 1 && (
        <Pager
          page={query.data.page}
          totalPages={query.data.totalPages}
          total={query.data.total}
          onChange={setPage}
        />
      )}

      {createOpen && (
        <CreateAccountModal
          onClose={() => setCreateOpen(false)}
          onCreated={async (result) => {
            setCreateOpen(false);
            setCreated(result);
            await Promise.all([
              qc.invalidateQueries({ queryKey: ['admin-users'] }),
              qc.invalidateQueries({ queryKey: ['admin-overview'] }),
            ]);
          }}
        />
      )}

      {created && <GeneratedPasswordDialog result={created} onClose={() => setCreated(null)} />}
    </div>
  );
}

function UserRow({ user }: { user: AdminUserSummary }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="glass-card p-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="bg-primary text-primary-ink flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[12px] font-bold">
          {user.name.charAt(0).toUpperCase()}
        </span>
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-2 truncate text-[13.5px] font-semibold">
            {user.name}
            {user.isPlatformAdmin && (
              <span className="text-accent bg-accent/10 rounded-full px-2 py-0.5 font-mono text-[10px] tracking-[0.04em] uppercase">
                Admin
              </span>
            )}
            {!user.isActive && (
              <span className="text-danger bg-danger/10 rounded-full px-2 py-0.5 font-mono text-[10px] tracking-[0.04em] uppercase">
                Disabled
              </span>
            )}
          </p>
          <p className="text-muted truncate text-[11.5px]">{user.email}</p>
        </div>

        <div className="text-ink-soft shrink-0 text-right text-[11.5px]">
          <p>
            {user.workspaces.length} workspace{user.workspaces.length === 1 ? '' : 's'}
          </p>
          <p className="text-muted">
            {user.lastLoginAt
              ? `Last seen ${new Date(user.lastLoginAt).toLocaleDateString()}`
              : 'Never signed in'}
          </p>
        </div>

        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="btn-ghost shrink-0 px-2.5 py-1.5 text-[11.5px]"
        >
          {open ? 'Hide' : 'Details'}
        </button>
      </div>

      {open && (
        <div className="border-line mt-3 border-t pt-3">
          <dl className="grid gap-2 text-[12px] sm:grid-cols-2">
            <div>
              <dt className="text-muted font-mono text-[10.5px] tracking-[0.04em] uppercase">
                Content role
              </dt>
              <dd className="capitalize">{user.role}</dd>
            </div>
            <div>
              <dt className="text-muted font-mono text-[10.5px] tracking-[0.04em] uppercase">
                Created
              </dt>
              <dd>{new Date(user.createdAt).toLocaleString()}</dd>
            </div>
          </dl>

          <p className="text-muted mt-3 font-mono text-[10.5px] tracking-[0.04em] uppercase">
            Workspaces
          </p>
          <ul className="mt-1.5 space-y-1">
            {user.workspaces.length === 0 && (
              <li className="text-ink-soft text-[12px]">
                None yet — one is created automatically at sign-in.
              </li>
            )}
            {user.workspaces.map((ws) => (
              <li key={ws.id} className="flex items-center gap-2 text-[12.5px]">
                <Building2 size={12} className="text-muted shrink-0" />
                <span className="truncate font-medium">{ws.name}</span>
                <span className="text-muted text-[11px] capitalize">{ws.role}</span>
                {ws.needsSetup && (
                  <span className="text-ink-soft bg-ink/6 rounded-full px-2 py-0.5 font-mono text-[10px] tracking-[0.04em] uppercase">
                    Setup pending
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Create account
// ─────────────────────────────────────────────────────────────────

/** Plan options for a new account: Free, or a paid tier with no expiry. */
const PLAN_OPTIONS: Array<{ id: SubscriptionPlanId; label: string }> = SUBSCRIPTION_PLANS.map(
  (p) => ({ id: p.id, label: p.name }),
);

function CreateAccountModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (result: AdminCreateUserResult) => void;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [workspaceName, setWorkspaceName] = useState('');
  const [password, setPassword] = useState('');
  const [plan, setPlan] = useState<SubscriptionPlanId>('free');
  const [periodDays, setPeriodDays] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    if (!name.trim()) return setError('Enter the person’s name.');
    if (!email.trim()) return setError('Enter an email address.');
    if (password && password.length < 8) {
      return setError(
        'A password you set must be at least 8 characters — or leave it blank to generate one.',
      );
    }

    setBusy(true);
    try {
      const result = await api.admin.createUser({
        name: name.trim(),
        email: email.trim(),
        role: 'admin',
        ...(password ? { password } : {}),
        ...(workspaceName.trim() ? { workspaceName: workspaceName.trim() } : {}),
        ...(plan !== 'free' ? { plan } : {}),
        ...(plan !== 'free' && periodDays ? { periodDays: Number(periodDays) } : {}),
      });
      onCreated(result);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create the account.');
      setBusy(false);
    }
  };

  return (
    <Modal title="Create account" onClose={onClose}>
      <p className="text-ink-soft text-body-sm">
        The account becomes the <strong>owner</strong> of its own workspace and is sent through
        onboarding the first time they sign in.
      </p>

      <div className="mt-5 space-y-4">
        <div>
          <label htmlFor="cu-name" className="field-label">
            Name
          </label>
          <input
            id="cu-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="field-input"
            placeholder="Ada Lovelace"
            autoFocus
          />
        </div>

        <div>
          <label htmlFor="cu-email" className="field-label">
            Email address
          </label>
          <input
            id="cu-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="field-input"
            placeholder="ada@example.com"
          />
        </div>

        <div>
          <label htmlFor="cu-workspace" className="field-label">
            Workspace name <span className="text-muted font-normal">(optional)</span>
          </label>
          <input
            id="cu-workspace"
            value={workspaceName}
            onChange={(e) => setWorkspaceName(e.target.value)}
            className="field-input"
            placeholder={name ? `${name}'s workspace` : "Their name's workspace"}
            maxLength={80}
          />
          <p className="text-muted mt-1 text-[10.5px]">
            Workspace names are unique across the whole server.
          </p>
        </div>

        <div>
          <label htmlFor="cu-password" className="field-label">
            Initial password <span className="text-muted font-normal">(optional)</span>
          </label>
          <input
            id="cu-password"
            type="text"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="field-input"
            placeholder="Leave blank to generate one"
            autoComplete="off"
          />
          <p className="text-muted mt-1 text-[10.5px]">
            A generated password is shown once, after creating. Either way, share it securely — the
            account holder can change it during onboarding.
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="cu-plan" className="field-label">
              Plan
            </label>
            <select
              id="cu-plan"
              value={plan}
              onChange={(e) => setPlan(e.target.value as SubscriptionPlanId)}
              className="field-input"
            >
              {PLAN_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {plan !== 'free' && (
            <div>
              <label htmlFor="cu-days" className="field-label">
                Term in days <span className="text-muted font-normal">(blank = no expiry)</span>
              </label>
              <input
                id="cu-days"
                type="number"
                min={1}
                max={3650}
                value={periodDays}
                onChange={(e) => setPeriodDays(e.target.value)}
                className="field-input"
                placeholder="e.g. 365"
              />
            </div>
          )}
        </div>
      </div>

      {error && (
        <p className="border-danger/20 bg-danger/8 text-danger mt-4 rounded-lg border px-3 py-2 text-[12px]">
          {error}
        </p>
      )}

      <div className="mt-6 flex items-center justify-end gap-2">
        <button type="button" onClick={onClose} className="btn-ghost px-3 py-2 text-[12.5px]">
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void submit()}
          disabled={busy}
          className="btn-primary px-4 py-2 text-[13px]"
        >
          {busy ? (
            <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
          ) : (
            <Plus size={14} />
          )}
          {busy ? 'Creating…' : 'Create account'}
        </button>
      </div>
    </Modal>
  );
}

/**
 * The generated password is shown exactly once — the server stores only a
 * bcrypt hash, so this dialog is the only chance to record it.
 */
function GeneratedPasswordDialog({
  result,
  onClose,
}: {
  result: AdminCreateUserResult;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const password = result.generatedPassword;

  const copy = async () => {
    const text = `BlogForger account\nEmail: ${result.user.email}\nPassword: ${password ?? '(the one you set)'}\nSign in: ${window.location.origin}/login`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Modal title="Account created" onClose={onClose}>
      <p className="text-body-sm">
        <strong>{result.user.name}</strong> owns <strong>{result.workspace.name}</strong>.
      </p>

      {password ? (
        <div className="border-line bg-bg-soft/60 rounded-btn mt-4 border p-3">
          <p className="text-muted flex items-center gap-1.5 font-mono text-[10.5px] tracking-[0.04em] uppercase">
            <KeyRound size={11} /> Initial password — shown once
          </p>
          <code className="mt-1.5 block font-mono text-[15px] font-semibold break-all">
            {password}
          </code>
        </div>
      ) : (
        <p className="text-ink-soft mt-3 text-[12.5px]">They sign in with the password you set.</p>
      )}

      <p className="text-ink-soft mt-3 text-[12.5px]">
        Pass this on securely. It is not stored anywhere in readable form, so if it is lost the
        password has to be reset by creating the account again.
      </p>

      <div className="mt-6 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={() => void copy()}
          className="btn-ghost px-3 py-2 text-[12.5px]"
        >
          {copied ? <Check size={13} /> : <Copy size={13} />}
          {copied ? 'Copied' : 'Copy details'}
        </button>
        <button type="button" onClick={onClose} className="btn-primary px-4 py-2 text-[13px]">
          Done
        </button>
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────
// Workspaces
// ─────────────────────────────────────────────────────────────────

function WorkspacesTab() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [planTarget, setPlanTarget] = useState<AdminWorkspaceSummary | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['admin-workspaces', { page, search }],
    queryFn: () => api.admin.workspaces({ page, limit: PAGE_SIZE, ...(search ? { search } : {}) }),
  });

  const workspaces = query.data?.workspaces ?? [];

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative min-w-[220px] flex-1 sm:max-w-[320px]">
          <Search size={14} className="text-muted absolute top-1/2 left-3 -translate-y-1/2" />
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search workspaces"
            aria-label="Search workspaces"
            className="field-input pl-9"
          />
        </div>
      </div>

      {flash && (
        <p className="border-success/30 bg-success/6 text-body-sm rounded-btn mt-4 border px-3.5 py-3">
          {flash}
        </p>
      )}

      {query.isLoading && <p className="text-muted mt-6 text-[13px]">Loading workspaces…</p>}
      {query.isError && <p className="text-danger mt-6 text-[13px]">Could not load workspaces.</p>}

      {!query.isLoading && workspaces.length === 0 && (
        <div className="glass-card mt-6 p-8 text-center">
          <p className="text-[14px] font-semibold">No workspaces found.</p>
        </div>
      )}

      {workspaces.length > 0 && (
        <div className="mt-6 overflow-x-auto">
          <table className="w-full border-collapse text-left text-[12.5px]">
            <thead>
              <tr className="text-muted font-mono text-[10.5px] tracking-[0.04em] uppercase">
                <th className="border-line border-b px-3 py-2 font-normal">Workspace</th>
                <th className="border-line border-b px-3 py-2 font-normal">Owner</th>
                <th className="border-line border-b px-3 py-2 font-normal">Members</th>
                <th className="border-line border-b px-3 py-2 font-normal">Plan</th>
                <th className="border-line border-b px-3 py-2 font-normal">Setup</th>
                <th className="border-line border-b px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {workspaces.map((ws) => (
                <tr key={ws.id} className="hover:bg-ink/2">
                  <td className="border-line border-b px-3 py-2.5">
                    <span className="font-semibold">{ws.name}</span>
                    {!ws.isActive && (
                      <span className="text-danger bg-danger/10 ml-2 rounded-full px-2 py-0.5 font-mono text-[10px] tracking-[0.04em] uppercase">
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="border-line border-b px-3 py-2.5">
                    <span className="block truncate">{ws.ownerName ?? '—'}</span>
                    <span className="text-muted block truncate text-[11px]">
                      {ws.ownerEmail ?? 'no owner'}
                    </span>
                  </td>
                  <td className="border-line border-b px-3 py-2.5">{ws.memberCount}</td>
                  <td className="border-line border-b px-3 py-2.5">
                    <span className="font-medium capitalize">{ws.plan}</span>
                    <span className="text-muted block text-[11px]">
                      {ws.currentPeriodEnd
                        ? `ends ${new Date(ws.currentPeriodEnd).toLocaleDateString()}`
                        : ws.subscriptionStatus
                          ? `${ws.subscriptionStatus} · no expiry`
                          : 'default'}
                    </span>
                  </td>
                  <td className="border-line border-b px-3 py-2.5">
                    {ws.needsSetup ? (
                      <span className="text-ink-soft bg-ink/6 rounded-full px-2 py-0.5 font-mono text-[10px] tracking-[0.04em] uppercase">
                        Pending
                      </span>
                    ) : (
                      <span className="text-ink-soft inline-flex items-center gap-1 text-[11.5px]">
                        <Check size={11} /> Done
                      </span>
                    )}
                  </td>
                  <td className="border-line border-b px-3 py-2.5 text-right">
                    <button
                      type="button"
                      onClick={() => setPlanTarget(ws)}
                      className="btn-ghost px-2.5 py-1.5 text-[11.5px]"
                    >
                      <Sparkles size={12} />
                      Set plan
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {query.data && query.data.totalPages > 1 && (
        <Pager
          page={query.data.page}
          totalPages={query.data.totalPages}
          total={query.data.total}
          onChange={setPage}
        />
      )}

      {planTarget && (
        <AssignPlanModal
          workspace={planTarget}
          onClose={() => setPlanTarget(null)}
          onAssigned={async (message) => {
            setPlanTarget(null);
            setFlash(message);
            await Promise.all([
              qc.invalidateQueries({ queryKey: ['admin-workspaces'] }),
              qc.invalidateQueries({ queryKey: ['admin-overview'] }),
            ]);
          }}
        />
      )}
    </div>
  );
}

function AssignPlanModal({
  workspace,
  onClose,
  onAssigned,
}: {
  workspace: AdminWorkspaceSummary;
  onClose: () => void;
  onAssigned: (message: string) => void;
}) {
  const [plan, setPlan] = useState<SubscriptionPlanId>(workspace.plan);
  const [periodDays, setPeriodDays] = useState('');
  const [error, setError] = useState<string | null>(null);

  const assign = useMutation({
    mutationFn: () =>
      api.admin.setPlan(workspace.id, {
        plan,
        ...(plan !== 'free' && periodDays ? { periodDays: Number(periodDays) } : {}),
      }),
    onSuccess: (result) => {
      onAssigned(
        result.plan === 'free'
          ? `${workspace.name} is back on the free plan.`
          : `${workspace.name} is now on ${result.plan}${
              result.subscription?.currentPeriodEnd
                ? ` until ${new Date(result.subscription.currentPeriodEnd).toLocaleDateString()}`
                : ' with no expiry'
            }.`,
      );
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : 'Could not assign that plan.'),
  });

  return (
    <Modal title={`Plan for ${workspace.name}`} onClose={onClose}>
      <p className="text-ink-soft text-body-sm">
        Assigned directly, with no payment — the workspace is treated as paid until the term ends
        (or indefinitely). Switching cancels the current subscription first.
      </p>

      <div className="mt-5 space-y-4">
        <div>
          <label htmlFor="ap-plan" className="field-label">
            Plan
          </label>
          <select
            id="ap-plan"
            value={plan}
            onChange={(e) => setPlan(e.target.value as SubscriptionPlanId)}
            className="field-input"
          >
            {PLAN_OPTIONS.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {plan !== 'free' && (
          <div>
            <label htmlFor="ap-days" className="field-label">
              Term in days <span className="text-muted font-normal">(blank = no expiry)</span>
            </label>
            <input
              id="ap-days"
              type="number"
              min={1}
              max={3650}
              value={periodDays}
              onChange={(e) => setPeriodDays(e.target.value)}
              className="field-input"
              placeholder="e.g. 365"
            />
            <p className="text-muted mt-1 text-[10.5px]">
              The expiry worker ends the term on that date and the workspace drops back to free.
            </p>
          </div>
        )}

        {plan === 'free' && workspace.plan !== 'free' && (
          <p className="border-danger/20 bg-danger/8 text-danger rounded-lg border px-3 py-2 text-[12px]">
            This cancels {workspace.name}&apos;s current {workspace.plan} subscription immediately.
          </p>
        )}
      </div>

      {error && <p className="text-danger mt-4 text-[12.5px]">{error}</p>}

      <div className="mt-6 flex items-center justify-end gap-2">
        <button type="button" onClick={onClose} className="btn-ghost px-3 py-2 text-[12.5px]">
          Cancel
        </button>
        <button
          type="button"
          onClick={() => assign.mutate()}
          disabled={assign.isPending}
          className="btn-primary px-4 py-2 text-[13px]"
        >
          {assign.isPending ? (
            <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
          ) : (
            <Sparkles size={13} />
          )}
          {plan === 'free' && workspace.plan !== 'free' ? 'Move to free' : 'Assign plan'}
        </button>
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────────────────────────
// Shared bits
// ─────────────────────────────────────────────────────────────────

function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-ink/40 fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="surface-row max-h-[90vh] w-full max-w-md overflow-y-auto p-5">
        <div className="flex items-start justify-between gap-4">
          <h3 className="text-ink text-[15px] font-bold tracking-tight">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="btn-ghost h-8 w-8 rounded-lg px-0"
          >
            ✕
          </button>
        </div>
        <div className="mt-3">{children}</div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Sends
//
// How delivery is going across the whole deployment. Counts and a timeline
// only: no recipient addresses, no rendered bodies, no failure text. Two
// things about this screen are easy to misread, so both are labelled in the
// UI as well as here:
//
//   * "Stranded" is not a backlog. Cancelling a send leaves its remaining
//     recipients `queued` in the table forever, so the server reports them
//     separately — otherwise the queue figure would grow with every
//     cancellation and never drain.
//   * Throughput is measured, and `throttle.perMinute` is the ceiling it is
//     running against. The throttled executor's own history is pruned to a
//     one-minute window, so it cannot be read back; the configured ceiling is
//     the only baseline available.
// ─────────────────────────────────────────────────────────────────

const SEND_STATUS_LABEL: Record<EmailSendStatus, string> = {
  queued: 'Queued',
  sending: 'Sending',
  sent: 'Sent',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const SEND_STATUS_TONE: Record<EmailSendStatus, string> = {
  queued: 'bg-line/40 text-ink-soft',
  sending: 'bg-accent/12 text-accent',
  sent: 'bg-success/12 text-success',
  failed: 'bg-danger/12 text-danger',
  cancelled: 'bg-line/40 text-muted',
};

function SendStatusBadge({ status }: { status: EmailSendStatus }) {
  return (
    <span
      className={`rounded-full px-2 py-0.5 font-mono text-[10px] tracking-[0.04em] uppercase ${SEND_STATUS_TONE[status]}`}
    >
      {SEND_STATUS_LABEL[status]}
    </span>
  );
}

/** Compact duration: 45s, 6m 30s, 2h 5m. Null renders as an em dash. */
function formatDuration(ms: number | null): string {
  if (ms === null) return '—';
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}

function SendsTab() {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<EmailSendStatus | ''>('');

  const overview = useQuery({
    queryKey: ['admin-sends-overview'],
    queryFn: () => api.admin.sendsOverview(),
  });

  const query = useQuery({
    queryKey: ['admin-sends', { page, status }],
    queryFn: () => api.admin.sends({ page, limit: PAGE_SIZE, ...(status ? { status } : {}) }),
  });

  const sends = query.data?.sends ?? [];

  return (
    <div>
      <SendsSummary data={overview.data} loading={overview.isLoading} />

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-1.5">
          {(['', 'queued', 'sending', 'sent', 'failed', 'cancelled'] as const).map((value) => (
            <button
              key={value || 'all'}
              type="button"
              onClick={() => {
                setStatus(value);
                setPage(1);
              }}
              className={`rounded-full px-3 py-1 text-[11.5px] font-semibold transition-colors ${
                status === value
                  ? 'bg-accent text-white'
                  : 'bg-line/40 text-ink-soft hover:text-ink'
              }`}
            >
              {value ? SEND_STATUS_LABEL[value] : 'All'}
            </button>
          ))}
        </div>
      </div>

      {query.isLoading && <p className="text-muted mt-6 text-[13px]">Loading sends…</p>}
      {query.isError && <p className="text-danger mt-6 text-[13px]">Could not load sends.</p>}

      {!query.isLoading && sends.length === 0 && (
        <div className="glass-card mt-6 p-8 text-center">
          <p className="text-[14px] font-semibold">No sends yet.</p>
          <p className="text-ink-soft mt-1 text-[12.5px]">
            A send appears here once a workspace queues one.
          </p>
        </div>
      )}

      {sends.length > 0 && (
        <div className="mt-6 overflow-x-auto">
          <table className="w-full border-collapse text-left text-[12.5px]">
            <thead>
              <tr className="text-muted font-mono text-[10.5px] tracking-[0.04em] uppercase">
                <th className="border-line border-b px-3 py-2 font-normal">Send</th>
                <th className="border-line border-b px-3 py-2 font-normal">Workspace</th>
                <th className="border-line border-b px-3 py-2 font-normal">Status</th>
                <th className="border-line border-b px-3 py-2 font-normal">Recipients</th>
                <th className="border-line border-b px-3 py-2 font-normal">Timeline</th>
                <th className="border-line border-b px-3 py-2 font-normal">Rate</th>
              </tr>
            </thead>
            <tbody>
              {sends.map((send) => (
                <SendRow key={send.id} send={send} />
              ))}
            </tbody>
          </table>

          {query.data && query.data.totalPages > 1 && (
            <Pager
              page={page}
              totalPages={query.data.totalPages}
              total={query.data.total}
              onChange={setPage}
            />
          )}
        </div>
      )}
    </div>
  );
}

function SendRow({ send }: { send: AdminSendMetrics }) {
  const { counts } = send;
  const audiences = send.targets.map((t) => t.name ?? '(deleted list)').join(', ');

  return (
    <tr className="border-line/60 border-b align-top">
      <td className="max-w-[260px] px-3 py-3">
        <p className="truncate font-semibold">{send.emailSubject ?? '(deleted email)'}</p>
        <p className="text-muted mt-0.5 truncate font-mono text-[10.5px]">
          {audiences || 'no audience'} · {send.senderName ?? 'sender removed'}
        </p>
      </td>
      <td className="px-3 py-3">{send.workspaceName}</td>
      <td className="px-3 py-3">
        <SendStatusBadge status={send.status} />
      </td>
      <td className="px-3 py-3">
        <p className="font-mono text-[11.5px]">
          <span className="text-success">{counts.delivered}</span>
          <span className="text-muted"> / </span>
          <span className="text-danger">{counts.failed}</span>
          <span className="text-muted"> / </span>
          <span>{counts.queued}</span>
        </p>
        <p className="text-muted mt-0.5 text-[10.5px]">
          delivered / failed / pending of {counts.total}
        </p>
        {send.abandoned > 0 && (
          <p className="text-muted mt-0.5 text-[10.5px]">
            {send.abandoned} stranded by cancellation
          </p>
        )}
      </td>
      <td className="px-3 py-3">
        <p className="font-mono text-[11.5px]">
          {send.status === 'sending' ? 'running ' : ''}
          {formatDuration(send.runningMs)}
        </p>
        <p className="text-muted mt-0.5 text-[10.5px]">
          waited {formatDuration(send.queuedMs)} for the job
        </p>
      </td>
      <td className="px-3 py-3">
        <p className="font-mono text-[11.5px]">
          {send.recipientsPerSecond === null ? '—' : `${send.recipientsPerSecond}/s`}
        </p>
        <p className="text-muted mt-0.5 text-[10.5px]">measured over the run</p>
      </td>
    </tr>
  );
}

function SendsSummary({
  data,
  loading,
}: {
  data: AdminSendMetricsOverview | undefined;
  loading: boolean;
}) {
  if (loading) return <p className="text-muted text-[13px]">Loading send totals…</p>;
  if (!data) return null;

  const { sends, recipients, throttle } = data;
  const settled = recipients.delivered + recipients.failed;

  return (
    <div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <Counter
          label="Sends"
          value={sends.total}
          hint={`${sends.queued} queued · ${sends.sending} in flight`}
        />
        <Counter
          label="Delivered"
          value={recipients.delivered}
          hint={`of ${recipients.total} recipients`}
        />
        <Counter label="Failed" value={recipients.failed} hint={`${settled} settled in total`} />
        <Counter label="Pending" value={recipients.queued} hint="waiting on the sender job" />
        <Counter
          label="Stranded"
          value={recipients.abandoned}
          hint={`across ${sends.cancelled} cancelled send(s)`}
        />
      </div>

      <p className="text-ink-soft mt-4 text-[11.5px]">
        Sends: {sends.sent} sent · {sends.failed} failed · {sends.cancelled} cancelled ·{' '}
        {sends.queued} queued · {sends.sending} sending. The sender job delivers at most{' '}
        {throttle.perMinute}/min using {throttle.maxDegreeOfParallelism} parallel workers,
        {` ${throttle.batchSize}`} recipients per batch, up to {throttle.maxPerRun} per sweep across{' '}
        {throttle.maxSendsPerRun} sends — so a healthy send's measured rate sits under{' '}
        {throttle.perMinute}/min.
      </p>

      {recipients.abandoned > 0 && (
        <p className="text-muted mt-2 text-[11.5px]">
          {recipients.abandoned} recipients were stranded: the send was called off before the job
          reached them, and cancellation does not rewrite recipient rows. They are counted here
          rather than as pending, so the pending figure always drains.
        </p>
      )}
    </div>
  );
}

function Pager({
  page,
  totalPages,
  total,
  onChange,
}: {
  page: number;
  totalPages: number;
  total: number;
  onChange: (page: number) => void;
}) {
  return (
    <div className="border-line mt-5 flex items-center justify-between gap-3 border-t pt-4">
      <p className="text-muted text-[11.5px]">
        Page {page} of {totalPages} · {total} total
      </p>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onChange(page - 1)}
          disabled={page <= 1}
          className="btn-ghost px-3 py-1.5 text-[12px] disabled:opacity-40"
        >
          Previous
        </button>
        <button
          type="button"
          onClick={() => onChange(page + 1)}
          disabled={page >= totalPages}
          className="btn-ghost px-3 py-1.5 text-[12px] disabled:opacity-40"
        >
          Next
        </button>
      </div>
    </div>
  );
}
